import { castArray, clone, isError, isNumeric } from '../views/utils'
import { buildServerQuery } from '../views/utils/query'
import deepEquals from 'fast-deep-equal'
import { hashPagination } from './utils'
import initSync from './sync'
import manipulations from './manipulations'
import modules from './modules'
import mutations from './mutations'
import { services } from './services'

const inject = (storeModule, manipulationKey) => ({ commit, getters }) => async (response) => {
  if (isError(response)) throw new Error(response)

  const results = getResults(response)

  if (storeModule.auto.has('mutations') && results) {
    manipulations[manipulationKey](storeModule)({ commit, getters }, results)
  }

  return response
}

const actions = {
  async CLEAR_STATE({ commit }) {
    Object
      .keys(mutations)
      .filter(mutationName => mutationName.startsWith('CLEAR_'))
      .forEach(commit)
  },
  SYNC_GLOBAL(store) {
    initSync(modules, store)
  },
}

const displayError = storeModule => store => (error) => {
  // do not recurisively throw errors for errors
  if ((!storeModule ||
    !storeModule.storeSuffix ||
    storeModule.storeSuffix !== 'ERROR') &&
    (error && error.message.startsWith('Unexpected token'))) {
    error.message = 'Can\'t connect to the server'
  }

  store.dispatch('ERROR', error)

  return Promise.reject(error)
}

function getResults(response) {
  if (!response) return null
  if (response.data) return castArray(response.data)
  return castArray(response)
}

Object.values(modules).forEach((storeModule) => {
  if (!storeModule.auto.has('actions')) return

  const service = services[storeModule.serviceName]
  const addSuffix = actionName => `${actionName}_${storeModule.storeSuffix}`
  const addEntries = inject(storeModule, 'add')
  const addEntriesDirectly = store => response => addEntries(store, false)(response)
  const removeEntries = inject(storeModule, 'remove')
  const errorHandler = displayError(storeModule)
  const queryFetches = []

  // TODO: migrate to fully async/await
  /* eslint-disable promise/prefer-await-to-then */
  const actionsTemplate = {
    CREATE: (store, data) => service
      .create(data)
      .then(addEntries(store))
      .catch(errorHandler(store)),

    FIND_RAW: (store, query) => service
      .find(query)
      .then(addEntriesDirectly(store))
      .catch(errorHandler(store)),

    FIND: ({ dispatch }, query) =>
      dispatch(addSuffix('FIND_RAW'), query)
        .then(getResults),

    GET: (store, id) => service
      .get(id)
      .then(addEntriesDirectly(store))
      .catch(errorHandler(store)),

    // similar to GET/FIND but with checking if it's already fetched
    FETCH: async ({ commit, dispatch }, query = {}) => {
      // const storeEntries = stateList(state[storeModule.storeKey])

      // GET of one entry by ID
      if (isNumeric(query)) {
        const id = parseInt(query, 10)

        // TODO: re-enable GET requests when they have joins
        // return dispatch(addSuffix('GET'), id)

        return dispatch(addSuffix('FIND_RAW'), { id })

        // TODO: work out caching for GET requests
        // NOTE:: we cannot use single store check since these do not show if the record had
        // its relationships loaded from the back-end
        // return storeEntries[id]
        //   ? Promise.resolve(storeEntries[id])
        //   : dispatch(addSuffix('GET'), parseInt(query, 10))
      }

      // FIND by query
      const refetchTime = Date.now() - storeModule.cacheFor
      const serverQuery = buildServerQuery(query)
      const { $skip, ...queryWithoutPagination } = serverQuery
      const paginationString = hashPagination(serverQuery)
      const recordedFetch = queryFetches
        .find(recordedFetch => deepEquals(recordedFetch.query, queryWithoutPagination))

      if (recordedFetch) {
        if (recordedFetch.fetchedAt[paginationString] > refetchTime) {
          return []
        }

        recordedFetch.fetchedAt[paginationString] = Date.now()
      } else {
        queryFetches.push({
          query: clone(queryWithoutPagination),
          fetchedAt: {
            [paginationString]: Date.now(),
          },
        })
      }

      const result = await dispatch(addSuffix('FIND_RAW'), serverQuery)

      if (!Array.isArray(result.data)) return result

      const { total, skip } = result
      commit(addSuffix('ADD_PAGE_OBJECT'), {
        query,
        records: result.data,
        skip,
        total,
      })
      // const queryHash = hashQuery({ ...query, $skip: skip })

      return result
    },

    DELETE: (store, { id }) => service
      .remove(id)
      .then(removeEntries(store))
      .catch(errorHandler(store)),

    UPDATE: (store, { id, ...data }) => service
      .update(id, data)
      .then(addEntries(store))
      .catch(errorHandler(store)),

    PATCH: (store, { id, ...data }) => service
      .patch(id, data)
      .then(addEntries(store))
      .catch(errorHandler(store)),
  }
  /* eslint-enable promise/prefer-await-to-then */

  const storeModuleActions = Object
    .fromEntries(Object
      .entries(actionsTemplate)
      .map(([actionName, action]) => [addSuffix(actionName), action]))

  Object.assign(actions, storeModuleActions)
})

export default actions
